import {
  getErrorMessage,
  getErrorStatusAndCode,
  isBrowserOffline,
} from '@pogokid/util'

export interface ErrorResponse {
  error: ErrorData
}

export interface ErrorData {
  message: string
  code?: string
  meta?: object
  errors: Array<{ message: string; code?: string }>
}

export function isErrorData(data: unknown): data is ErrorData {
  return (
    typeof data === 'object' &&
    !!data &&
    'message' in data &&
    typeof data.message === 'string'
  )
}

export function isErrorResponse(
  data: unknown
): data is ErrorResponse {
  return (
    typeof data === 'object' &&
    !!data &&
    'error' in data &&
    isErrorData(data.error)
  )
}

export class AppError extends Error {
  code?: string
  status: number = 418
  meta?: object
  errors: Omit<ErrorData, 'errors'>[]

  constructor(message: string) {
    super(message)
    this.message = message
    this.errors = []
  }

  static fromResponse(res: Response, data: unknown): AppError {
    let error: AppError | undefined = undefined
    if (isErrorResponse(data)) {
      error = new AppError(data.error.message)
      error.code = data.error.code
      error.errors = data.error.errors
      error.meta = data.error.meta
    }
    if (!error && typeof data === 'string' && data.length > 0) {
      error = new AppError(data)
    }
    if (
      !error &&
      typeof data === 'object' &&
      !!data &&
      'message' in data
    ) {
      error = new AppError(String(data.message))
    }
    if (!error) {
      const statusLabel = res.statusText
        ? `${res.status} ${res.statusText}`
        : `HTTP ${res.status}`
      error = new AppError(
        `Unexpected error response (${statusLabel})`
      )
    }
    error.status = res.status
    return error
  }

  /**
   * Build the error for a request that never reached the server.
   *
   * Only call this where that is already known — on a `fetch` rejection.
   * Each engine words that rejection differently ("Failed to fetch" in
   * Chrome, "NetworkError when attempting to fetch resource." in
   * Firefox, "Load failed" in Safari), so the rejection itself is the
   * signal; matching its message would leave most browsers unhandled.
   */
  static asConnectionError(error: unknown): AppError {
    const appError = AppError.fromError(error)
    if (!appError.isConnectionError() && !appError.isOffline()) {
      appError.code = isBrowserOffline() ? 'offline' : 'connection'
      appError.status = 503
    }
    return appError
  }

  static fromError(error: unknown, message?: string): AppError {
    const apiError = new AppError(
      message ?? getErrorMessage(error)
    )
    if (error instanceof AppError) {
      apiError.status = error.status
      apiError.code = error.code
      apiError.errors = error.errors ?? []
      apiError.meta = error.meta
    } else {
      const { status, code } = getErrorStatusAndCode(error)
      apiError.status = status
      apiError.code = code
    }
    if (error instanceof Error) {
      apiError.stack = error.stack
    }
    apiError.errors.push({
      message: getErrorMessage(error),
    })
    return apiError
  }

  isUnauthorized() {
    return this.code === 'unauthorized' || this.status === 401
  }

  isConflictError() {
    return this.status === 409 || this.code === 'conflict'
  }

  isConnectionError() {
    return this.code === 'connection'
  }

  isOffline() {
    return this.code === 'offline'
  }

  /**
   * True when we never got an answer from the server, or the answer was
   * the server reporting a fault of its own.
   *
   * The distinction that matters to a reader: what they asked for may be
   * perfectly fine, we just could not reach it — so trying again is
   * worth their time, and we must not blame their input.
   */
  isUnreachable() {
    return (
      this.isConnectionError() ||
      this.isOffline() ||
      this.status >= 500
    )
  }

  isForbiddenEventPushed() {
    return this.code === 'forbidden_event_pushed'
  }

  log() {
    console.error(this.toString(), this.stack)
  }

  toString() {
    return `${this.message} (status: ${this.status}, code: ${this.code})`
  }

  toJSON() {
    if (this.errors) {
      return {
        status: this.status,
        code: this.code,
        message: this.message,
        errors: this.errors,
      }
    } else {
      return {
        status: this.status,
        code: this.code,
        message: this.message,
      }
    }
  }
}
